import { useTranslation } from "react-i18next";
import i18n from "../i18n";

/**
 * Whether a run covered the whole suite, or a slice of it.
 *
 * This is the single most misleading thing about a run list without it. "5
 * failed" out of 213 cases and "5 failed" out of a twelve-case `-k LED` slice
 * are not the same fact, and a table that shows them in the same column invites
 * exactly that comparison - a short run reads as a good day.
 *
 * FULL IS DELIBERATELY UNMARKED. Whole suites are the normal case and the
 * common one; badging every row teaches people to stop seeing badges, and the
 * one that matters is the exception. Only slices and unknowns carry a mark.
 */
export function scopeLabel(r: { scope: string; k: string; files: string; marker: string }) {
  if (r.scope !== "partial") return "";
  // IN WORDS (2026-09-16). This used to be what was typed - "-k SB and not
  // WALK" - so it could be pasted back into run.sh. A run list is read by
  // testers, and command-line syntax is the one thing the style guide keeps off
  // the screen; the exact selection is in the title for whoever repeats it.
  if (r.k) return i18n.t("scope.matching", { filter: r.k });
  if (r.files) return i18n.t("scope.onlyFile", { file: r.files.split(" ")[0].replace(/\.py$/, "") });
  return i18n.t("scope.slice");
}

export default function Scope({ run }: {
  run: { scope: string; k: string; files: string; marker: string };
}) {
  const { t } = useTranslation();
  if (run.scope === "full") return null;

  if (run.scope === "unknown") {
    return (
      <span
        // Not "full". Every run from before run.sh recorded the selection lands
        // here, and calling those whole suites would relabel the history that
        // is most worth trusting.
        title={t("scope.notRecordedTitle")}
        className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400"
      >
        {t("scope.notRecorded")}
      </span>
    );
  }

  const label = scopeLabel(run);
  return (
    <span
      title={[
        run.marker && t("scope.marker", { marker: run.marker }),
        run.k && `-k ${run.k}`,
        run.files && t("scope.files", { files: run.files }),
      ].filter(Boolean).join(" · ") || t("scope.sliceTitle")}
      className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200"
    >
      {label}
    </span>
  );
}
